/* eslint-disable no-prototype-builtins */
/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import {
    executeAwaitableHandlerChain,
    makeHandlerChainAwaitable,
    replaceInChain,
} from "@insite/client-framework/HandlerCreator";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadProducts from "@insite/client-framework/Store/Data/Products/Handlers/LoadProducts";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import {
    chain,
    GetUnfilteredProducts,
    LoadRealTimePrices,
    PopulateApiParameter,
} from "@insite/client-framework/Store/Pages/ProductList/Handlers/DisplayProducts";
import { RealTimePricingModel } from "@insite/client-framework/Types/ApiModels";

export const XngPopulateApiParameter: any = (props: any) => {
    const filters = props.result.productFilters;

    const {
        query,
        brandIds,
        productLineIds,
        priceFilters,
        attributeValueIds,
        searchWithinQueries,
        pageCategoryId,
        pageBrandId,
        pageProductLineId,
        ...apiParameter
    } = filters;
    props.apiParameter = {
        ...apiParameter,
        search: query,
        categoryId: filters.categoryId || filters.pageCategoryId,
        searchWithin: searchWithinQueries?.join(" "),
        includeSuggestions: filters.includeSuggestions !== false,
        brandIds: filters.pageBrandId && !brandIds ? [filters.pageBrandId] : brandIds,
        productLineIds: filters.pageProductLineId ? [filters.pageProductLineId] : productLineIds,
        priceFilters,
        attributeValueIds,
        expand: props.expandTokens || [],
        applyPersonalization: true,
        includeAttributes: ["includeOnProduct"],
        includeProductsInSubCategories: !!(
            filters.categoryId?.length ||
            filters.pageCategoryId?.length ||
            props.result.catelogPage?.categoryId.length
        ),
    };

    // XNG-896: add restrictAttributesToCategory to product api for L2  categories
    const categoryId =
        filters?.pageBrandId?.length > 0
            ? `${props.apiParameter.categoryId}:${filters.pageBrandId}`
            : props.apiParameter.categoryId;
    const category = getCategoryState(props.getState(), categoryId).value;
    const restrictAttributesToCategory = props.parameter?.["restrictAttributesToCategory"];
    if (
        props.apiParameter?.categoryId &&
        (category?.properties?.isLevelOneCategory === "false" || !category?.properties?.isLevelOneCategory)
    ) {
        props.apiParameter = {
            ...props.apiParameter,
            restrictAttributesToCategory,
        };
    }
};

export const XngGetUnfilteredProducts: any = async (props: any) => {
    const unfilteredApiParameter = { ...props.apiParameter };
    delete unfilteredApiParameter.searchWithin;
    if (!props.result.productFilters.pageBrandId) {
        delete unfilteredApiParameter.brandIds;
    }
    if (!props.result.productFilters.pageProductLineId) {
        delete unfilteredApiParameter.productLineIds;
    }
    delete unfilteredApiParameter.priceFilters;
    delete unfilteredApiParameter.attributeValueIds;
    if (
        !props.result.productFilters.pageCategoryId &&
        !unfilteredApiParameter?.hasOwnProperty("includeProductsInSubCategories")
    ) {
        delete unfilteredApiParameter.categoryId;
    }
    unfilteredApiParameter.stockedItemsOnly = undefined;
    unfilteredApiParameter.previouslyPurchasedProducts = undefined;

    props.result.unfilteredApiParameter = unfilteredApiParameter;
    props.result.filteredApiParameter = { ...props.apiParameter };

    const unfilteredProducts = getProductsDataView(props.getState(), unfilteredApiParameter).value;

    if (unfilteredProducts) {
        return;
    }

    await executeAwaitableHandlerChain(loadProducts, unfilteredApiParameter, props);
};

export const XngLoadRealTimePrices: any = async (props: any) => {
    const {
        result: { productInfosByProductId },
    } = props;
    const productIds = Object.keys(productInfosByProductId);
    if (productIds.length === 0) {
        return;
    }

    const loadRealTimePricingParameter: Parameters<typeof loadRealTimePricing>[0] = {
        productPriceParameters: productIds.map(o => productInfosByProductId[o]!),
        onSuccess: realTimePricing => {
            props.dispatch({
                type: "Pages/ProductList/CompleteLoadRealTimePricing",
                realTimePricing,
            });
            props.pricingLoaded = true;
        },
        onError: () => {
            props.dispatch({
                type: "Pages/ProductList/FailedLoadRealTimePricing",
            });
            props.pricingLoaded = true;
        },
        onComplete(realTimePricingProps) {
            if (realTimePricingProps.apiResult) {
                this.onSuccess?.(realTimePricingProps.apiResult);
            } else if (realTimePricingProps.error) {
                this.onError?.(realTimePricingProps.error);
            }
        },
    };

    if (IS_SERVER_SIDE && getIsWebCrawler()) {
        const awaitableLoadRealTimePricing = makeHandlerChainAwaitable<
            Parameters<typeof loadRealTimePricing>[0],
            RealTimePricingModel
        >(loadRealTimePricing);
        const realTimePricing = await awaitableLoadRealTimePricing({
            productPriceParameters: loadRealTimePricingParameter.productPriceParameters,
        })(props.dispatch, props.getState);
        if (realTimePricing) {
            loadRealTimePricingParameter.onSuccess?.(realTimePricing);
        } else {
            loadRealTimePricingParameter.onError?.(realTimePricing);
        }
    } else {
        props.dispatch(loadRealTimePricing(loadRealTimePricingParameter));
    }
    const session = props.getState().context?.session;
    const isAuthenticated = session && (session.isAuthenticated || session.rememberMe) && !session.isGuest;
    if (getSettingsCollection(props.getState()).productSettings.inventoryIncludedWithPricing && isAuthenticated) {
        await waitFor(() => !!props.pricingLoaded);
    }
};

replaceInChain(chain, PopulateApiParameter, XngPopulateApiParameter);
replaceInChain(chain, GetUnfilteredProducts, XngGetUnfilteredProducts);
replaceInChain(chain, LoadRealTimePrices, XngLoadRealTimePrices);
